import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, withRouter } from 'react-router';

import EditFormPage from 'modules/ui/editform/EditFormPage';
import EditFormTextField from 'modules/ui/editform/fields/EditFormTextField';
import EditFormSelectField from 'modules/ui/editform/fields/EditFormSelectField';
import EditFormGroupFields from 'modules/ui/editform/subForm/EditFormGroupFields';
import EditFormSeparatorField from 'modules/ui/editform/fields/EditFormSeparatorField';
import EditFormGridComponent from 'modules/ui/editform/subForm/EditFormGridComponent';
import EditFormComponentMassSomethingField from 'app/admin/common/component/EditFormComponentMassSomethingField';

import AspectService from 'app/admin/aspect/service/AspectService';
import MeasurementService from 'app/admin/measurement/service/MeasurementService';
import ModelValidationLogService from 'app/admin/modelValidationLog/service/ModelValidationLogService';
import StorageTaskManagerService from 'app/admin/taskManager/service/StorageTaskManagerService';
import WhatIfService from 'app/admin/whatIf/service/WhatIfService';
import ModelValidationEditService from '../service/ModelValidationEditService';
import UserGroupService from 'app/admin/userGroup/service/UserGroupService';

const blockClassName = 'rounded-8 border px-16 pt-10 pb-4 mb-16';
const blockTitleClassName = 'py-8';

const config = data => {
	const storageTaskManagerService = new StorageTaskManagerService();

	return {
		key: 'modelValidationEditForm',
		topic: 'modelValidation',
		service: ModelValidationEditService,
		fields: initData => {
			const fields = [
				{
					key: 'name',
					component: EditFormTextField,
					required: true,
					readOnly: data.id !== 'new'
				},
				{
					key: 'userGroup',
					component: EditFormSelectField,
					items: () => UserGroupService.getListSelection()
				},
				{
					key: 'measurement',
					component: EditFormSelectField,
					required: true,
					items: () => MeasurementService.getListSelection()
				},
				{
					key: 'aspect',
					component: EditFormSelectField,
					required: true,
					items: () => AspectService.getListSelection()
				},
				{
					key: 'modelValidationLog',
					component: EditFormSelectField,
					required: true,
					items: () => ModelValidationLogService.getListSelection()
				},
				{
					key: 'whatIf',
					component: EditFormSelectField,
					required: false,
					items: () => WhatIfService.getListSelection()
				},
				{
					key: 'modelValidationGroup',
					component: EditFormGroupFields,
					className: blockClassName,
					defaultValue: {
						name: null,
					},
					dependsOn: ['aspect', 'measurement'],
					visibleIf: dep => {
						const aspect = Array.isArray(dep) ? dep[0] : dep;
						const measurement = Array.isArray(dep) ? dep[1] : null;
						return aspect !== null && measurement !== null;
					},
					fields: [
						{
							key: 'sepTolerance',
							text: 'Tolerance',
							component: EditFormSeparatorField,
							className: blockTitleClassName + ' pb-16',
							isControlledInput: false
						},
						{
							key: 'tolerances',
							component: EditFormGridComponent,
							defaultValue: {
								name: null,
								value: 0.5,
							},
							fields: [
								{
									key: 'name',
									component: EditFormSelectField,
									required: true,
									type: 'enum',
									items: (dto, field) => storageTaskManagerService.getNamesForModelValidationTolerance(dto, field, 'both'),
									dependsOn: ['aspect', 'measurement'],
									visibleIf: dep => {
										const aspect = Array.isArray(dep) ? dep[0] : dep;
										const measurement = Array.isArray(dep) ? dep[1] : null;
										return aspect !== null && measurement !== null;
									},
								},
								{
									key: 'value',
									component: EditFormTextField,
									required: true,
								},
							]
						}
					]
				},
				{
					key: 'stateInjectGroup',
					component: EditFormGroupFields,
					className: blockClassName,
					defaultValue: {
						name: null,
						type: null,
						value: null,
					},
					dependsOn: 'aspect',
					visibleIf: aspect => aspect !== null,
					fields: [
						{
							key: 'sepStateInject',
							text: 'State Inject',
							component: EditFormSeparatorField,
							className: blockTitleClassName + ' pb-16',
							isControlledInput: false
						},
						{
							key: 'stateInject',
							component: EditFormGridComponent,
							defaultValue: { name: null, type: null, value: null },
							fields: [
								{
									key: 'name',
									component: EditFormSelectField,
									required: true,
									type: 'enum',
									items: (dto, field) => storageTaskManagerService.getNamesForAspect(dto, field, dto?.aspect, field?.aspectAttributes || 'inputs'),
									dependsOn: 'aspect',
									visibleIf: aspect => aspect !== null,
								},
								{
									key: 'type',
									component: EditFormSelectField,
									required: true,
									type: 'enum',
									items: [
										{ label: 'Variable', value: 'Variable' },
										{ label: 'ComponentMoleFlow', value: 'ComponentMoleFlow' },
										{ label: 'ComponentMassFrac', value: 'ComponentMassFrac' },
										{ label: 'ComponentMassFlow', value: 'ComponentMassFlow' },
										{ label: 'DistillationCurve', value: 'DistillationCurve' },
										{ label: 'SpreadSheet', value: 'SpreadSheet' }
									]
								},
								{
									key: 'value',
									component: EditFormTextField,
									required: true,
									type: 'number',
									dependsOn: 'type',
									visibleIf: type => type === 'Variable' || type === 'SpreadSheet' || type === 'DistillationCurve' || type === 'ComponentMoleFlow'
								},
								{
									key: 'value',
									component: EditFormComponentMassSomethingField,
									required: true,
									dependsOn: 'type',
									visibleIf: type => type === 'ComponentMassFrac' || type === 'ComponentMassFlow'
								}
							]
						}
					]
				}
			];

			return fields;
		}
	};
};

function ModelValidationEditForm() {
	const { id } = useParams();
	const myConfig = config({ id });

	const [dto, setDto] = useState(null);
	const taskManager = useMemo(() => new StorageTaskManagerService(), []);
	const methodsRef = useRef(null);
	const fillingRef = useRef(false);

	useEffect(() => {
		let cancelled = false;

		async function fillTypes() {
			if (fillingRef.current) return;
			if (dto && Array.isArray(dto.stateInject) && dto.stateInject.length > 0 && dto.aspect) {
				fillingRef.current = true;

				for (let i = 0; i < dto.stateInject.length; i++) {
					if (cancelled) break;

					const si = dto.stateInject[i];
					if (si && si.name) {
						const t = await taskManager.getTypeForAspectInput(si.name, dto.aspect, 'inputs');
						const newType = t || null;
						const path = `stateInject.${i}.type`;
						const current = methodsRef.current?.getValues(path);
						if (current !== newType) {
							methodsRef.current?.setValue(path, newType, { shouldDirty: true, shouldTouch: false, shouldValidate: false });
						}
					}
				}

				fillingRef.current = false;
			}
		}

		fillTypes();
		return () => { cancelled = true; fillingRef.current = false; };
	}, [dto?.aspect, dto?.stateInject ? JSON.stringify(dto.stateInject.map(si => si?.name ?? null)) : null, taskManager]);

	return (
		<EditFormPage
			config={myConfig}
			onWatch={d => setDto(d)}
			onDataChanged={(data, methods) => { methodsRef.current = methods; }}
		/>
	);
}

export default withRouter(ModelValidationEditForm);